<template>
    <component
        :is="as"
        :to="card.link?.url"
        :href="!isNuxtLink ? card.link?.url : undefined"
        class="flex h-full w-full rounded-xl bg-white text-grind shadow-card"
        :class="mainClasses"
    >
        <div
            v-if="hasImageSlot || card.image"
            class="relative"
            :class="imageContainerClasses"
        >
            <slot name="image">
                <StoryblokImage
                    v-if="card.image"
                    :src="card.image.src"
                    :alt="card.image.alt"
                    class="h-full w-full object-cover"
                    :class="imageClasses"
                    itemprop="image"
                    :sizes="card.image.sizes ?? '342px'"
                    :image-width="card.image.width ?? 352"
                    :image-height="card.image.height ?? 352"
                    :quality="card.image.quality ?? 90"
                />
            </slot>
        </div>

        <div
            class="flex flex-1 flex-col justify-between"
            :class="contentClasses"
        >
            <div class="flex flex-col">
                <slot name="tags">
                    <TagGroup
                        v-if="card.tags"
                        :tags="card.tags"
                        class="mb-2 flex-wrap"
                    />
                </slot>

                <slot name="title">
                    <div
                        v-if="card.title"
                        class="mb-3.5 text-xl font-bold"
                        itemprop="headline"
                    >
                        {{ card.title }}
                    </div>
                </slot>

                <slot name="subtitle">
                    <div
                        v-if="card.shortDescription"
                        class="mb-2.5 text-sm font-bold text-drip"
                    >
                        {{ card.shortDescription }}
                    </div>
                </slot>

                <slot name="description">
                    <WysiwygContent
                        v-if="card.description"
                        class="mb-4"
                        :content="card.description"
                    />
                </slot>
            </div>

            <div class="mt-4">
                <slot name="footer">
                    <NuxtLink v-if="card.link" :to="card.link.url">
                        {{ card.link.label }}
                    </NuxtLink>
                </slot>
            </div>
        </div>
    </component>
</template>

<script setup lang="ts">
import CardType from '~/types/ui/CardType';

withDefaults(
    defineProps<{
        card: CardType;
        size?: string;
        as?: string;
        mainClasses?: string;
        imageContainerClasses?: string;
        imageClasses?: string;
        contentClasses?: string;
    }>(),
    {
        size: 'small',
        as: 'div',
        mainClasses: 'flex-col',
        imageContainerClasses: 'rounded-t-[10px] h-[235px] bg-gray-100',
        imageClasses: 'rounded-t-[10px] overflow-hidden',
        contentClasses: 'p-6',
    },
);

const slots = useSlots();
const hasImageSlot = computed(() => !!slots.image);
const isNuxtLink = computed(
    () => typeof resolveComponent('NuxtLink') !== 'undefined',
);
</script>
